import React, { useEffect, useRef, useState } from 'react';
import { Circle, Group, Image, Rect, Text } from 'react-konva';
import type Konva from 'konva';
import type { Node } from '../../../core/models/scene';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';
import { getIconUrl, getServiceColor } from '../../../domains/network/shapes/aws-icon-registry';
import { Logger } from '../../../utils/logger';
import { StackBadge, StackShadow, readStackCount } from './decorators/StackDecorator';
import { useThemeMode } from '../../../react-app/theme/ThemeProvider';

const logger = Logger.getLoggerWithPrefix('ManifestSvgNode');

const LIGHT_CARD_FILL = '#ffffff';
const LIGHT_CARD_STROKE = '#94a3b8';
const LIGHT_TEXT_PRIMARY = '#1e293b';
const LIGHT_TEXT_SECONDARY = '#64748b';
const DARK_CARD_FILL = '#111C33';
const DARK_CARD_STROKE = '#334155';
const DARK_TEXT_PRIMARY = '#E5EDF8';
const DARK_TEXT_SECONDARY = '#A8B3C7';
const DARK_STACK_REAR_FILL = '#0F1A30';
const DARK_STACK_FRONT_FILL = '#16233D';

/**
 * Returns a contrasting text colour ('#ffffff' or '#1e293b') for a
 * given hex background. Computes relative luminance via the simple
 * sRGB formula — bright backgrounds get dark text, dim backgrounds
 * get light text. Used to keep node labels readable when colorBy /
 * heatmap rules paint a dark fill onto the card.
 */
function pickContrastTextColor(hex: string): string {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length !== 3 && cleaned.length !== 6) return '#1e293b';
  const expand = cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned;
  const r = parseInt(expand.slice(0, 2), 16);
  const g = parseInt(expand.slice(2, 4), 16);
  const b = parseInt(expand.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return '#1e293b';
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luma < 140 ? '#ffffff' : '#1e293b';
}

function humanizeResourceType(value: string): string {
  const segments = value.split('/').filter(Boolean);
  const lastSegment = segments.length > 0 ? segments[segments.length - 1] : value;
  const spaced = lastSegment
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  if (!spaced) return value;
  return spaced
    .split(/\s+/)
    .map((part) => {
      const upper = part.toUpperCase();
      if (['API', 'AWS', 'CDN', 'CPU', 'DNS', 'GCP', 'IAM', 'IP', 'KMS', 'NAT', 'SQL', 'VPC'].includes(upper)) {
        return upper;
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
}

function resourceTypeLabel(resourceType: string, manifestDefaults: Record<string, unknown>): string {
  const providerLabel = typeof manifestDefaults.name === 'string' ? manifestDefaults.name.trim() : '';
  const dialectId = typeof manifestDefaults.dialectId === 'string' ? manifestDefaults.dialectId.trim() : '';
  const hostingProvider = typeof manifestDefaults.hostingProvider === 'string' ? manifestDefaults.hostingProvider.trim() : '';
  if (providerLabel && (dialectId || hostingProvider)) return providerLabel;
  return humanizeResourceType(resourceType);
}

function normalizedColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return value.trim().toLowerCase();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

const CARD_MIN_HEIGHT = 60;
const CARD_VERTICAL_PADDING = 12;
const CARD_TEXT_GAP = 3;
const CARD_NAME_FONT_SIZE = 12;
const CARD_NAME_LINE_HEIGHT = 1.15;
const CARD_SECONDARY_FONT_SIZE = 10;
const CARD_SECONDARY_HEIGHT = 12;
const CARD_AVERAGE_CHAR_WIDTH = 0.58;

function estimateWrappedTextHeight(text: string, maxWidth: number): number {
  const charsPerLine = Math.max(
    1,
    Math.floor(maxWidth / (CARD_NAME_FONT_SIZE * CARD_AVERAGE_CHAR_WIDTH)),
  );
  const lineCount = text.split(/\r?\n/).reduce((count, rawLine) => {
    const words = rawLine.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return count + 1;

    let lineCountForSegment = 1;
    let usedChars = 0;
    for (const word of words) {
      if (word.length > charsPerLine) {
        if (usedChars > 0) {
          lineCountForSegment += 1;
          usedChars = 0;
        }
        lineCountForSegment += Math.floor((word.length - 1) / charsPerLine);
        usedChars = ((word.length - 1) % charsPerLine) + 1;
        continue;
      }
      if (usedChars === 0) {
        usedChars = word.length;
      } else if (usedChars + 1 + word.length <= charsPerLine) {
        usedChars += 1 + word.length;
      } else {
        lineCountForSegment += 1;
        usedChars = word.length;
      }
    }

    return count + lineCountForSegment;
  }, 0);
  return Math.ceil(lineCount * CARD_NAME_FONT_SIZE * CARD_NAME_LINE_HEIGHT);
}

function resolveExplicitManifestColor(
  topLevel: unknown,
  attr: unknown,
  manifestDefault: unknown,
  fallbackDefault: string,
): string | undefined {
  const defaultColor = normalizedColor(manifestDefault) ?? fallbackDefault.toLowerCase();
  const attrColor = normalizedColor(attr);
  const topLevelColor = normalizedColor(topLevel);

  if (topLevelColor && attrColor && topLevelColor !== attrColor) {
    return topLevel as string;
  }
  if (attrColor && attrColor !== defaultColor) {
    return attr as string;
  }
  if (topLevelColor && !attrColor && topLevelColor !== defaultColor) {
    return topLevel as string;
  }
  return undefined;
}

function resolveNonNegativeNumber(values: unknown[], fallback: number): number {
  for (const value of values) {
    const numeric =
      typeof value === 'number'
        ? value
        : typeof value === 'string'
          ? Number(value)
          : Number.NaN;
    if (Number.isFinite(numeric) && numeric >= 0) return numeric;
  }
  return fallback;
}

function normalizeDashArray(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const dash = value
    .map((entry) => typeof entry === 'number' ? entry : Number(entry))
    .filter((entry) => Number.isFinite(entry) && entry > 0);
  return dash.length > 0 ? dash : undefined;
}

function resolveBoolean(values: unknown[]): boolean | undefined {
  for (const value of values) {
    if (typeof value === 'boolean') return value;
  }
  return undefined;
}

function resolveManifestDash(
  nodeRecord: Record<string, unknown>,
  attrs: Record<string, unknown>,
  manifestDefaults: Record<string, unknown>,
): { dash?: number[]; explicit: boolean } {
  const dashEnabled = resolveBoolean([
    nodeRecord.dashEnabled,
    attrs.dashEnabled,
    attrs.borderDashEnabled,
    manifestDefaults.dashEnabled,
  ]);
  const dash = normalizeDashArray(nodeRecord.dash)
    ?? normalizeDashArray(attrs.dash)
    ?? normalizeDashArray(attrs.borderDash)
    ?? normalizeDashArray(manifestDefaults.dash);

  if (dashEnabled === false) return { explicit: true };
  if (dash) return { dash, explicit: true };
  return { explicit: dashEnabled === true };
}

/**
 * Subset of the orgchart `useViewRules` ComputedNodeStyle shape that
 * `ManifestSvgNode` actually applies. Kept structural (not imported)
 * so this renderer doesn't take a hard dependency on the orgchart
 * domain package and any caller can construct one inline.
 */
export interface ManifestLensStyle {
  filterOpacity?: number;
  filterVisible?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  badges?: Array<{ icon?: string; color?: string; tooltip?: string }>;
  labels?: Array<{ text: string; position?: string }>;
}

interface ManifestSvgNodeProps {
  node: Node;
  manifest: ShapeManifest<unknown>;
  selected: boolean;
  /**
   * Optional color override from a view profile's scoring result. When set,
   * replaces the accent stripe and icon tile color so the node visually
   * reflects its top-ranked rule match (e.g. red for critical security risk,
   * gold for high cost).
   */
  overrideAccent?: string;
  /**
   * Optional dim flag for when a view profile rule is focused and this node
   * isn't matched by it. Rendered with reduced opacity.
   */
  dimmed?: boolean;
  /**
   * Lens style overlay computed by useViewRules — colour overrides,
   * badges, secondary labels, and filter opacity from agent tools like
   * `color_by`, `heatmap`, `badge`, `label`, `filter`. When undefined
   * the node renders with its manifest defaults plus profile overrides.
   */
  lensStyle?: ManifestLensStyle;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => void;
  onDoubleClick?: (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => void;
  onStartTextEdit?: (textNode: Konva.Text) => void;
  editingField?: 'name' | null;
  canWrite?: boolean;
}

// Cache loaded HTMLImageElements by URL so every node that shares an icon
// (e.g. all EC2 instances point to the same AWS icon URL) only fetches once.
const iconCache = new Map<string, HTMLImageElement>();

/**
 * Loads an external image URL into an HTMLImageElement. Returns null until
 * the image is ready. Safe to call from render — the Image load happens in
 * an effect and the component re-renders when it arrives.
 *
 * Unlike SVG data-URI loading, plain external URLs are not sandboxed by the
 * browser, so cross-origin CDN fetches (e.g. AWS icons from jsDelivr) work.
 */
function useExternalImage(url: string | null): HTMLImageElement | null {
  const [image, setImage] = useState<HTMLImageElement | null>(() =>
    url ? iconCache.get(url) ?? null : null,
  );

  useEffect(() => {
    if (!url) {
      setImage(null);
      return;
    }
    const cached = iconCache.get(url);
    if (cached && cached.complete) {
      setImage(cached);
      return;
    }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      iconCache.set(url, img);
      setImage(img);
    };
    img.onerror = (err) => {
      logger.warn(`Icon image failed to load: ${url}`, err);
      setImage(null);
    };
    img.src = url;
  }, [url]);

  return image;
}

/**
 * Generic renderer for shape manifests that paints native Konva primitives
 * plus an externally-loaded icon image. This is the last-resort path in
 * NodesRenderer — any domain whose shape manifest doesn't have a hand-rolled
 * React component falls through here.
 *
 * Renders a card-shape layout:
 *   [icon tile] [name]
 *   [         ] [TYPE]
 * With a colored accent stripe on the left edge.
 */
export const ManifestSvgNode: React.FC<ManifestSvgNodeProps> = ({
  node,
  manifest,
  selected,
  overrideAccent,
  dimmed,
  lensStyle,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onDoubleClick,
  onStartTextEdit,
  editingField = null,
  canWrite = true,
}) => {
  const { mode } = useThemeMode();
  const isDarkMode = mode === 'dark';
  const containerNameTextRef = useRef<Konva.Text | null>(null);
  const cardNameTextRef = useRef<Konva.Text | null>(null);
  const width = (node.w ?? manifest.defaultDimensions?.width ?? 160) as number;
  const height = (node.h ?? manifest.defaultDimensions?.height ?? 60) as number;

  // Extract data from the runtime node. Resolution priority:
  //   1. The manifest's defaultProps() — provides the canonical `resourceType`
  //      (e.g. `'ec2'`, `'router'`) regardless of whether the runtime node's
  //      `type` is the bare service name (hydrator path) or the manifest id
  //      (click-to-create path like `'aws_ec2'`).
  //   2. Explicit overrides on `node.attrs` (e.g. profile scoring, user
  //      edits via the property panel).
  //   3. Top-level `node.type` / `node.name` fallback.
  const nodeRecord = node as Node & Record<string, unknown>;
  const rawManifestDefaults = manifest.defaultProps();
  const manifestDefaults: Record<string, unknown> = isRecord(rawManifestDefaults) ? rawManifestDefaults : {};
  const attrs: Record<string, unknown> = isRecord(nodeRecord.attrs) ? nodeRecord.attrs : {};
  const resourceType = String(
    attrs.resourceType ?? manifestDefaults.resourceType ?? nodeRecord.type ?? '',
  );
  const resourceTypeDisplay = resourceTypeLabel(resourceType, manifestDefaults);
  const name = String(
    nodeRecord.name ?? attrs.name ?? manifestDefaults.name ?? nodeRecord.text ?? node.id,
  );
  // View profile scoring can override the manifest's default service color
  // so nodes visually reflect their top-ranked rule match.
  const accent = overrideAccent
    || (attrs.accentColor as string | undefined)
    || (manifestDefaults.accentColor as string | undefined)
    || getServiceColor(resourceType)
    || '#64748b';
  const iconUrl = (attrs.iconUrl as string | undefined)
    ?? (manifestDefaults.iconUrl as string | undefined)
    ?? getIconUrl(resourceType);
  const iconImage = useExternalImage(iconUrl ?? null);

  // Scenario ghost overlay. When a what-if scenario is active the
  // projector stamps `attrs.scenarioStatus` on every affected node:
  //   - 'added'   → ghost green (proposed new resource)
  //   - 'removed' → ghost red, struck through (going away)
  //   - 'changed' → amber outline + badge
  // Ghost nodes render at 70% opacity with a dashed scenario-coloured
  // outline so they read as non-committed. The real colour accent
  // stays intact so the service identity is still obvious.
  const scenarioStatus = attrs.scenarioStatus as 'added' | 'removed' | 'changed' | undefined;
  const scenarioColor =
    scenarioStatus === 'added'
      ? '#10b981'
      : scenarioStatus === 'removed'
        ? '#ef4444'
        : scenarioStatus === 'changed'
          ? '#f59e0b'
          : null;
  const scenarioLabel =
    scenarioStatus === 'added'
      ? '+ ADD'
      : scenarioStatus === 'removed'
        ? '− REMOVE'
        : scenarioStatus === 'changed'
          ? '~ CHANGE'
          : null;
  const scenarioOpacity = scenarioStatus ? 0.72 : 1;
  const scenarioOverlayDash = scenarioStatus ? [6, 4] : undefined;

  // Lens style — applied AFTER scenario overlay so view-rule colours
  // win over scenario overlay tints when both are active. The renderer
  // uses these to override the card frame fill (background), border
  // stroke, label text colour, and to stack badge pills + secondary
  // labels under the resource name.
  const lensFill = lensStyle?.backgroundColor;
  const lensStroke = lensStyle?.borderColor;
  // When the lens supplies a fill colour but no explicit text colour,
  // pick a contrasting tone from the fill so dark categorical / heatmap
  // backgrounds don't render dark-on-dark text. Explicit lensStyle
  // textColor still wins so callers can override.
  const lensTextColor =
    lensStyle?.textColor ?? (lensFill ? pickContrastTextColor(lensFill) : undefined);
  const lensBadges = lensStyle?.badges ?? [];
  const lensLabels = lensStyle?.labels ?? [];
  // Compass Explorer stamps `attrs.lens = { color, badge, dataMissing }`
  // directly on imported resources (the bubble renderer reads it
  // already, the manifest renderer didn't yet). Surface the badge
  // text — typically a per-resource cost reading like "$93/mo" —
  // as a small contrast-safe pill in the top-right of the card so
  // L5/L4 resource views actually show cost / waste / utilisation
  // numbers without having to drill into each node.
  const compassLens = attrs.lens as
    | {
        color?: string;
        badge?: { label?: string; tone?: 'critical' | 'warn' | 'neutral' };
        dataMissing?: boolean;
      }
    | undefined;
  const compassLensBadge = compassLens?.badge?.label
    && !compassLens.dataMissing
    && compassLens.badge.label.toLowerCase() !== 'no cost'
    && compassLens.badge.label.toLowerCase() !== 'no data'
    ? compassLens.badge
    : null;
  // Finding severity stamped by `buildExplorerScene` from
  // `findingAttribution`. When present we paint a small severity dot
  // in the top-left of the card so high-severity resources (admin
  // policies, exposed buckets, etc.) jump out without having to open
  // the review queue. Cost anomalies are *not* mapped to a dot — the
  // dollar badge in the top-right already carries that signal.
  const findingSeverity = typeof attrs.findingSeverity === 'string'
    ? (attrs.findingSeverity as 'critical' | 'high' | 'medium' | 'low' | 'info')
    : null;
  const findingCount = typeof attrs.findingCount === 'number' ? attrs.findingCount : 0;
  const lensFilterOpacity = typeof lensStyle?.filterOpacity === 'number'
    ? lensStyle.filterOpacity
    : 1;
  const explicitFill = resolveExplicitManifestColor(
    nodeRecord.fillColor,
    attrs.fillColor,
    manifestDefaults.fillColor,
    LIGHT_CARD_FILL,
  );
  const explicitStroke = resolveExplicitManifestColor(
    nodeRecord.strokeColor,
    attrs.strokeColor,
    manifestDefaults.strokeColor,
    LIGHT_CARD_STROKE,
  );
  const explicitTextColor = resolveExplicitManifestColor(
    nodeRecord.textColor,
    attrs.textColor,
    manifestDefaults.textColor,
    LIGHT_TEXT_PRIMARY,
  );
  const cardFill = lensFill ?? explicitFill ?? (isDarkMode ? DARK_CARD_FILL : LIGHT_CARD_FILL);
  const cardStroke = lensStroke ?? explicitStroke ?? (isDarkMode ? DARK_CARD_STROKE : LIGHT_CARD_STROKE);
  const cardStrokeWidth = lensStroke ? 2 : resolveNonNegativeNumber([
    nodeRecord.strokeWidth,
    attrs.strokeWidth,
    attrs.borderWidth,
    manifestDefaults.strokeWidth,
  ], 1.5);
  const containerStrokeWidth = lensStroke ? 2 : resolveNonNegativeNumber([
    nodeRecord.strokeWidth,
    attrs.strokeWidth,
    attrs.borderWidth,
    manifestDefaults.strokeWidth,
  ], 2);
  const manifestDash = resolveManifestDash(nodeRecord, attrs, manifestDefaults);
  const containerDash = manifestDash.explicit ? manifestDash.dash : [8, 4];
  const primaryTextColor = lensTextColor ?? explicitTextColor ?? (isDarkMode ? DARK_TEXT_PRIMARY : LIGHT_TEXT_PRIMARY);
  const secondaryTextColor = lensTextColor ?? explicitTextColor ?? (isDarkMode ? DARK_TEXT_SECONDARY : LIGHT_TEXT_SECONDARY);

  // Card layout constants
  const ICON_SIZE = 32;
  const ICON_X = 10;
  const TEXT_X = ICON_X + ICON_SIZE + 10;
  const CARD_TEXT_WIDTH = Math.max(24, width - TEXT_X - 10);
  const cardNameTextHeight = estimateWrappedTextHeight(name, CARD_TEXT_WIDTH);
  const cardTextBlockHeight = cardNameTextHeight + CARD_TEXT_GAP + CARD_SECONDARY_HEIGHT;
  const cardHeight = Math.max(
    height,
    CARD_MIN_HEIGHT,
    ICON_SIZE + CARD_VERTICAL_PADDING * 2,
    cardTextBlockHeight + CARD_VERTICAL_PADDING * 2,
  );

  // Container shapes (VPC, Subnet) are dashed outlines with a colored header bar
  const isContainer = width >= 300 && height >= 150;
  const renderHeight = isContainer ? height : cardHeight;
  const ICON_Y = Math.round((renderHeight - ICON_SIZE) / 2);
  const cardTextY = Math.max(
    CARD_VERTICAL_PADDING,
    Math.round((renderHeight - cardTextBlockHeight) / 2),
  );
  const cardSecondaryTextY = cardTextY + cardNameTextHeight + CARD_TEXT_GAP;
  const abbrev = resourceTypeDisplay.slice(0, 3).toUpperCase() || '?';
  const containerLabel = resourceTypeDisplay.toLowerCase() === name.toLowerCase()
    ? name
    : `${resourceTypeDisplay}: ${name}`;
  const isEditingName = editingField === 'name';

  const startNameEdit = (
    textNode: Konva.Text | null,
    event: Konva.KonvaEventObject<Event>,
  ) => {
    if (!canWrite || !onStartTextEdit || !textNode) return;
    event.cancelBubble = true;
    onStartTextEdit(textNode);
  };

  return (
    <Group
      x={node.x ?? 0}
      y={node.y ?? 0}
      opacity={(dimmed ? 0.25 : 1) * scenarioOpacity * lensFilterOpacity}
      draggable={!!onDragStart}
      onClick={onClick}
      onTap={onClick}
      onDblClick={onDoubleClick}
      onDblTap={onDoubleClick}
      onDragStart={(e) => {
        const t = e.target;
        onDragStart?.({ x: t.x(), y: t.y() });
      }}
      onDragMove={(e) => {
        const t = e.target;
        onDragMove?.({ x: t.x(), y: t.y() });
      }}
      onDragEnd={(e) => {
        const t = e.target;
        onDragEnd?.({ x: t.x(), y: t.y() });
      }}
    >
      {isContainer ? (
        <>
          {/* Container: dashed outline + colored header bar */}
          <Rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill={cardFill}
            stroke={lensStroke ?? explicitStroke ?? accent}
            strokeWidth={containerStrokeWidth}
            dash={containerDash}
            cornerRadius={8}
          />
          <Rect x={0} y={0} width={width} height={32} fill={accent} cornerRadius={[8, 8, 0, 0]} />
          <Text
            ref={containerNameTextRef}
            x={10}
            y={10}
            width={width - 20}
            height={16}
            text={containerLabel}
            fontSize={12}
            fontStyle="bold"
            fontFamily="Arial, sans-serif"
            fill="#ffffff"
            wrap="none"
            ellipsis
            visible={!isEditingName}
            listening={canWrite && !isEditingName}
            onDblClick={(event) => startNameEdit(containerNameTextRef.current, event)}
            onDblTap={(event) => startNameEdit(containerNameTextRef.current, event)}
          />
        </>
      ) : (
        <>
          {/* Shadow stack behind the primary card — renders when the
              shape represents a clustered group (`attrs.stack.count > 1`). */}
          <StackShadow
            count={readStackCount(attrs)}
            width={width}
            height={renderHeight}
            rearFill={isDarkMode ? DARK_STACK_REAR_FILL : undefined}
            frontFill={isDarkMode ? DARK_STACK_FRONT_FILL : undefined}
            stroke={isDarkMode ? DARK_CARD_STROKE : undefined}
          />
          {/* Card frame */}
          <Rect
            x={0}
            y={0}
            width={width}
            height={renderHeight}
            fill={cardFill}
            stroke={cardStroke}
            strokeWidth={cardStrokeWidth}
            dash={manifestDash.dash}
            cornerRadius={6}
          />
          {/* Accent stripe */}
          <Rect x={0} y={0} width={4} height={renderHeight} fill={accent} cornerRadius={[2, 0, 0, 2]} />
          {/* Icon tile (colored square as background/fallback) */}
          <Rect x={ICON_X} y={ICON_Y} width={ICON_SIZE} height={ICON_SIZE} fill={accent} cornerRadius={6} />
          {/* Icon image loaded from CDN (overlays the colored square when ready) */}
          {iconImage && (
            <Image
              image={iconImage}
              x={ICON_X + 3}
              y={ICON_Y + 3}
              width={ICON_SIZE - 6}
              height={ICON_SIZE - 6}
              listening={false}
            />
          )}
          {/* Fallback abbreviation text (hidden when icon loads) */}
          {!iconImage && (
            <Text
              x={ICON_X}
              y={ICON_Y + ICON_SIZE / 2 - 5}
              width={ICON_SIZE}
              text={abbrev}
              fontSize={10}
              fontStyle="bold"
              fontFamily="Arial, sans-serif"
              fill="#ffffff"
              align="center"
              listening={false}
            />
          )}
          {/* Name (bold). Wraps inside the card and drives the visual card
              height so the resource/type label stays below it. */}
          <Text
            ref={cardNameTextRef}
            x={TEXT_X}
            y={cardTextY}
            width={CARD_TEXT_WIDTH}
            height={cardNameTextHeight}
            text={name}
            fontSize={CARD_NAME_FONT_SIZE}
            fontStyle="bold"
            fontFamily="Arial, sans-serif"
            lineHeight={CARD_NAME_LINE_HEIGHT}
            fill={primaryTextColor}
            wrap="word"
            visible={!isEditingName}
            listening={canWrite && !isEditingName}
            onDblClick={(event) => startNameEdit(cardNameTextRef.current, event)}
            onDblTap={(event) => startNameEdit(cardNameTextRef.current, event)}
          />
          {/* Type label (muted) — replaced by lens label when set so a
               `label by spend` rule shows the dollar value here instead
               of the type. */}
          <Text
            x={TEXT_X}
            y={cardSecondaryTextY}
            width={CARD_TEXT_WIDTH}
            height={CARD_SECONDARY_HEIGHT}
            text={lensLabels[0]?.text ?? resourceTypeDisplay}
            fontSize={CARD_SECONDARY_FONT_SIZE}
            fontFamily="Arial, sans-serif"
            fill={secondaryTextColor}
            wrap="none"
            ellipsis
            listening={false}
          />
          {/* Stack count badge (`×N`) — renders on top of the card so
              it stays visible even when a lens tints the card. */}
          <StackBadge count={readStackCount(attrs)} width={width} accent={accent} />
          {/* Severity dot — top-left of the card, coloured by the
              top severity across findings affecting this resource.
              The number inside is the finding count so a busy
              resource (e.g. an over-privileged role with 3 violations)
              stands out from one with a single low signal. */}
          {findingSeverity && (() => {
            const sevPalette: Record<string, { fill: string; stroke: string }> = {
              critical: { fill: '#ef4444', stroke: '#7f1d1d' },
              high: { fill: '#f97316', stroke: '#7c2d12' },
              medium: { fill: '#eab308', stroke: '#713f12' },
              low: { fill: '#0ea5e9', stroke: '#0c4a6e' },
              info: { fill: '#94a3b8', stroke: '#334155' },
            };
            const palette = sevPalette[findingSeverity] ?? sevPalette.info;
            const dotX = -4;
            const dotY = -4;
            const dotSize = 14;
            return (
              <Group listening={false}>
                <Circle
                  x={dotX + dotSize / 2}
                  y={dotY + dotSize / 2}
                  radius={dotSize / 2}
                  fill={palette.fill}
                  stroke={palette.stroke}
                  strokeWidth={1}
                />
                {findingCount > 1 && (
                  <Text
                    x={dotX}
                    y={dotY}
                    width={dotSize}
                    height={dotSize}
                    text={String(findingCount)}
                    fontSize={8}
                    fontStyle="bold"
                    fontFamily="Arial, sans-serif"
                    fill="#ffffff"
                    align="center"
                    verticalAlign="middle"
                  />
                )}
              </Group>
            );
          })()}
          {/* Compass per-resource lens badge — paints attrs.lens.badge
              (e.g. "$93/mo") as a contrast-safe pill in the top-right
              of the card. Tone palette mirrors the bubble badge so the
              two zoom levels read the same way. The heatmap-gradient
              colour is preserved as a 3px left-edge stripe inside the
              pill so the per-resource gradient stays legible. */}
          {compassLensBadge && (() => {
            const tone = compassLensBadge.tone ?? 'neutral';
            const palette: Record<string, { fill: string; stroke: string; text: string }> = {
              critical: { fill: '#fee2e2', stroke: '#ef4444', text: '#991b1b' },
              warn: { fill: '#fef3c7', stroke: '#f59e0b', text: '#92400e' },
              neutral: { fill: '#eef2ff', stroke: '#6366f1', text: '#3730a3' },
            };
            const p = palette[tone] ?? palette.neutral;
            const txt = compassLensBadge.label ?? '';
            const badgeW = Math.max(44, Math.min(96, 10 + txt.length * 5.5));
            const badgeX = width - badgeW - 6;
            const badgeY = 4;
            const edge = compassLens?.color ?? p.stroke;
            return (
              <>
                <Rect
                  x={badgeX}
                  y={badgeY}
                  width={badgeW}
                  height={14}
                  fill={p.fill}
                  stroke={p.stroke}
                  strokeWidth={1}
                  cornerRadius={7}
                  listening={false}
                />
                <Rect
                  x={badgeX}
                  y={badgeY}
                  width={3}
                  height={14}
                  fill={edge}
                  cornerRadius={[7, 0, 0, 7]}
                  listening={false}
                />
                <Text
                  x={badgeX + 2}
                  y={badgeY}
                  width={badgeW - 4}
                  height={14}
                  verticalAlign="middle"
                  text={txt}
                  fontSize={9}
                  fontStyle="bold"
                  fontFamily="Arial, sans-serif"
                  fill={p.text}
                  align="center"
                  listening={false}
                />
              </>
            );
          })()}
        </>
      )}
      {selected && (
        <Rect
          x={-2}
          y={-2}
          width={width + 4}
          height={renderHeight + 4}
          stroke="#3b82f6"
          strokeWidth={2}
          cornerRadius={8}
          fill="transparent"
          listening={false}
        />
      )}
      {scenarioColor && (
        <>
          {/* Scenario-coloured dashed outline — read as "proposed" */}
          <Rect
            x={-2}
            y={-2}
            width={width + 4}
            height={renderHeight + 4}
            stroke={scenarioColor}
            strokeWidth={2}
            dash={scenarioOverlayDash}
            cornerRadius={8}
            fill="transparent"
            listening={false}
          />
          {/* Top-right pill badge */}
          <Rect
            x={width - 62}
            y={-8}
            width={60}
            height={16}
            fill={scenarioColor}
            cornerRadius={8}
            listening={false}
          />
          <Text
            x={width - 62}
            y={-6}
            width={60}
            height={16}
            text={scenarioLabel ?? ''}
            fontSize={9}
            fontStyle="bold"
            fontFamily="Arial, sans-serif"
            fill="#ffffff"
            align="center"
            listening={false}
          />
          {scenarioStatus === 'removed' && (
            <Rect
              x={4}
              y={Math.round(renderHeight / 2) - 1}
              width={width - 8}
              height={2}
              fill={scenarioColor}
              opacity={0.7}
              listening={false}
            />
          )}
        </>
      )}
      {lensBadges.length > 0 && lensBadges.slice(0, 4).map((badge, i) => (
        <Group key={`lens-badge-${i}`} x={width - 14 - i * 16} y={-6}>
          <Rect
            x={0}
            y={0}
            width={14}
            height={14}
            fill={badge.color ?? '#3b82f6'}
            cornerRadius={7}
            listening={false}
          />
          <Text
            x={0}
            y={2}
            width={14}
            text={(badge.icon ?? '•').slice(0, 1).toUpperCase()}
            fontSize={9}
            fontStyle="bold"
            fontFamily="Arial, sans-serif"
            fill="#ffffff"
            align="center"
            listening={false}
          />
        </Group>
      ))}
      {/* M4 pattern label — only the anchor member draws the name pill so
          the label doesn't repeat across every participant. */}
      {(() => {
        const p = attrs.pattern as { label?: string; isAnchor?: boolean; hints?: { backgroundColor?: string } } | undefined;
        if (!p?.isAnchor || !p.label) return null;
        const label = p.label;
        const approxW = Math.min(width - 16, 14 + label.length * 6.2);
        return (
          <Group x={0} y={-22}>
            <Rect
              x={6}
              y={0}
              width={approxW}
              height={16}
              fill="#1e293b"
              cornerRadius={8}
              listening={false}
            />
            <Text
              x={6}
              y={2}
              width={approxW}
              text={label}
              fontSize={10}
              fontStyle="bold"
              fontFamily="Arial, sans-serif"
              fill="#ffffff"
              align="center"
              listening={false}
            />
          </Group>
        );
      })()}
      {/* M2 shared-service chips — inline row at bottom of the shape.
          Click dispatches a DOM CustomEvent 'compass:open-shared-drawer'
          with the resource id; SharedServiceDrawer listens for this. */}
      {(() => {
        const refs = attrs.sharedRefs as Array<{ resourceId: string; label: string; manifestId: string }> | undefined;
        if (!refs || refs.length === 0) return null;
        const MAX_CHIPS = 3;
        const chipH = 12;
        const chipGap = 4;
        const visible = refs.slice(0, MAX_CHIPS);
        const extra = refs.length - visible.length;
        let cursorX = 6;
        const chips: React.ReactNode[] = [];
        for (const ref of visible) {
          const label = ref.label.length > 12 ? ref.label.slice(0, 11) + '…' : ref.label;
          const chipW = Math.min(width - cursorX - 10, 16 + label.length * 5.5);
          chips.push(
            <Group
              key={ref.resourceId}
              x={cursorX}
              y={renderHeight + 4}
              onClick={(e: Konva.KonvaEventObject<MouseEvent>) => {
                e.cancelBubble = true;
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('compass:open-shared-drawer', {
                    detail: { resourceId: ref.resourceId },
                  }));
                }
              }}
              onTap={(e: Konva.KonvaEventObject<TouchEvent>) => {
                e.cancelBubble = true;
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('compass:open-shared-drawer', {
                    detail: { resourceId: ref.resourceId },
                  }));
                }
              }}
            >
              <Rect
                x={0}
                y={0}
                width={chipW}
                height={chipH}
                fill="#f1f5f9"
                stroke="#cbd5e1"
                strokeWidth={1}
                cornerRadius={6}
              />
              <Text
                x={4}
                y={1}
                width={chipW - 6}
                height={chipH}
                text={label}
                fontSize={9}
                fontFamily="Arial, sans-serif"
                fill="#334155"
                listening={false}
              />
            </Group>
          );
          cursorX += chipW + chipGap;
          if (cursorX > width - 20) break;
        }
        if (extra > 0) {
          const label = `+${extra}`;
          const chipW = 22;
          chips.push(
            <Group key="shared-overflow" x={cursorX} y={renderHeight + 4}>
              <Rect
                x={0}
                y={0}
                width={chipW}
                height={chipH}
                fill="#e2e8f0"
                cornerRadius={6}
                listening={false}
              />
              <Text
                x={0}
                y={1}
                width={chipW}
                height={chipH}
                text={label}
                fontSize={9}
                fontStyle="bold"
                fontFamily="Arial, sans-serif"
                fill="#475569"
                align="center"
                listening={false}
              />
            </Group>
          );
        }
        return <>{chips}</>;
      })()}
    </Group>
  );
};
